<template>
    <section class="cookie-consent flow__container-fluid flow__panel">
        <div>
            <h2>{{ myResources.title }}</h2>

            <component :is="schemeComponent" />

            <div class="cookie-consent__consent-all">
                <h4>{{ myResources.titleAllCookies }}</h4>

                <div class="cookie-consent__consent-all__buttons">
                    <hh-btn type="cta"
                            class="cookie-consent__cta--decline"
                            :text="commonResources.no"
                            @click="onDeclineAll"
                            :disabled="disableAcceptButtons" />
                    <hh-btn type="cta"
                            class="cookie-consent__cta--accept"
                            :text="commonResources.yes"
                            @click="onAcceptAll"
                            :disabled="disableAcceptButtons" />
                </div>
            </div>

            <hh-form class="cookie-consent__form">
                <hh-accordion-item :title="myResources.lblUsage"
                                   @toggle="setAcceptButtonDisabled">
                    <template>
                        <div class="cookie-consent__columns">
                            <hh-form-group def="application-functionality"
                                           :label-text="myResources.lblAppCore"
                                           stacked>
                                <hh-yes-no class="cookie-consent__radio--app-core"
                                           def="application-functionality"
                                           v-model="appCore"
                                           horizontal-layout
                                           reverse-options>
                                    <p>{{ myResources.summaryAppCore }}</p>
                                </hh-yes-no>
                            </hh-form-group>
                            <hh-form-group def="analytics"
                                           :label-text="myResources.lblAnalytics"
                                           stacked>
                                <hh-yes-no class="cookie-consent__radio--analytics"
                                           def="analytics"
                                           v-model="analytics"
                                           horizontal-layout
                                           reverse-options>
                                    <p>{{ myResources.summaryAnalytics }}</p>
                                </hh-yes-no>
                            </hh-form-group>
                        </div>
                        <div class="cookie-consent__action">
                            <hh-btn class="cookie-consent__cta"
                                    :text="commonResources.continue"
                                    type="cta"
                                    @click="onProceed"
                                    icon-class="fas fa-check" />
                        </div>
                    </template>
                </hh-accordion-item>
            </hh-form>
        </div>
    </section>
</template>

<script>

import CookieConsentManager from '@/cookie-consent-manager'
import ResourcesHelper from '@/components/resources-helper'

export default {
    props: {
        /**
         * Page to redirect to after completing cookie consent.
         * @type {Object}
         */
        redirect: {
            type: Object,
            default: null,
        },
    },

    mixins: [
        ResourcesHelper,
    ],

    data() {
        return {
            /**
             * @type {boolean}
             */
            appCore: false,

            /**
             * @type {boolean}
             */
            analytics: false,

            /**
             * @type {boolean}
             */
            allCookies: false,

            /**
             * @type {boolean}
             */
            disableAcceptButtons: false,
        }
    },

    computed: {

        /**
         * @returns {CookieConsentResources}
         */
        myResources() {
            return this.parseResources('views.cookieConsent')
        },

        /**
         * Retrieve scheme specific cookie consent text component.
         * @returns {Vue}
         */
        schemeComponent() {
            return this.$scheme.getCustomComponent('cookie-consent-text')
        },
    },

    methods: {
        /**
         * Set the disabled state of the yes / no accept buttons.
         *
         * @param {boolean} state
         */
        setAcceptButtonDisabled(state) {
            this.disableAcceptButtons = state
        },

        /**
         * Accept all cookies and continue.
         */
        async onAcceptAll() {
            this.allCookies = true
            await this.onProceed()
        },

        /**
         * Decline all cookies and continue.
         */
        async onDeclineAll() {
            this.allCookies = false
            await this.onProceed()
        },

        /**
         * Store consent data and proceed to the next screen. The next screen can be set in
         * the redirect prop or defaults to the Welcome screen.
         */
        async onProceed() {
            const cookieConsent = await CookieConsentManager.getConsentDataAsync()

            cookieConsent.analytics = this.allCookies || this.analytics
            cookieConsent.appCore = this.allCookies || this.appCore

            await CookieConsentManager.setConsentDataAsync(cookieConsent)

            this.$eventbus.$emit(this.$appevents.reloadCookieConsent)

            if (this.redirect) {
                this.$router.replace(this.redirect.fullPath)
            } else {
                this.$router.push({
                    name: 'Welcome',
                })
            }
        },
    },
}

</script>

<style lang="scss">

.cookie-consent {
    @include element('columns') {
        @include bp-min-mid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: $base-gutter;
        }
    }

    @include element('consent-all') {
        padding-top: 0.75rem;
        padding-bottom: 0.75rem;

        @include element('buttons') {
            @include bp-min-small {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: $base-gutter;
            }

            @include bp-min-mid {
                display: block;
            }
        }

        h4 {
            margin-bottom: 0;
        }

        .button--cta {
            float: none;
            display: block;
            width: 100%;

            @include bp-min-mid {
                display: inline-block;
                margin-right: $wide-gutter;
                min-width: 100px;
                width: auto;
            }
        }
    }

    @include element('action') {
        overflow: hidden;
    }

    .form__label {
        min-width: 200px;
        font-weight: bold;
    }

    .accordion-item {
        margin-top: $wide-gutter;
    }
}

</style>
